import { useEffect, useRef } from "react";


/**
 * If updateFn returns true, the animation timer is stopped.
 */
export function useAnimationTimer(
    updateFn: (() => boolean | undefined) | (() => void) | null,
    intervalMs: number,
): (node: Element | null) => (() => void) {

    const nodesRef = useRef<Element[]>([]);

    useEffect(() => {
        let running: boolean = true;
        let timeoutID: ReturnType<typeof setTimeout> | null = null;
        let animationFrameID: number | null = null;

        const update = () => {
            timeoutID = null;
            if (!running || !updateFn)
                return;

            animationFrameID = requestAnimationFrame(() => {
                animationFrameID = null;

                // Only run the update if any of the nodes are visible, or if there are no nodes.
                const nodes = nodesRef.current;
                let anyVisible = false;
                for (const node of nodes) {
                    const rect = node.getBoundingClientRect();
                    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                    if (rect.bottom >= 0 && rect.top <= viewportHeight) {
                        anyVisible = true;
                        break;
                    }
                }
                if (nodes.length === 0 || anyVisible) {
                    if (updateFn())
                        return;
                }

                timeoutID = setTimeout(update, intervalMs);
            });
        };
        update();

        return () => {
            running = false;
            if (timeoutID !== null) {
                clearTimeout(timeoutID);
            }
            if (animationFrameID !== null) {
                cancelAnimationFrame(animationFrameID);
            }
        };
    }, [updateFn, intervalMs]);

    return (node) => {
        if (node === null)
            return () => {};

        nodesRef.current.push(node);
        return () => {
            const index = nodesRef.current.indexOf(node);
            if (index >= 0) {
                nodesRef.current.splice(index, 1);
            }
        };
    };
}
